/**
 * Browser-side wrapper around `@elevenlabs/client`'s `Scribe.connect`
 */
import type { TranscriptWord } from 'js/src/client';
import { ApiService } from 'js/src/client';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { ScribeAudioFeed } from 'js/src/libs/realtime-transcription/scribe-audio-feed';

export type { TranscriptWord };

import type * as ScribeModule from '@elevenlabs/client';

const SCRIBE_MODEL_ID = 'scribe_v2_realtime';
const FINALIZE_TIMEOUT_MS = 5000;
const SCRIBE_SAMPLE_RATE = 16000;

export function clampWordsToDuration(
    words: TranscriptWord[],
    audioFileDurationMillis: number,
): TranscriptWord[] {
    if (!(audioFileDurationMillis > 0)) {
        return words;
    }
    // Word timings are in seconds; convert the media duration to seconds only
    // here, at the point of comparison.
    const maxDurationSecs = audioFileDurationMillis / 1000;
    const clamped: TranscriptWord[] = [];
    for (const word of words) {
        if (word.startTime >= maxDurationSecs) {
            continue;
        }
        clamped.push({ ...word, endTime: Math.min(word.endTime, maxDurationSecs) });
    }
    return clamped;
}

type ScribeConnection = ReturnType<typeof ScribeModule.Scribe.connect>;

export class ScribeRealtimeClient {
    readonly #micTrack: MediaStreamTrack;
    readonly #feed: ScribeAudioFeed;
    #connection: ScribeConnection | null = null;
    #isOpen = false;
    #pendingChunks: string[] = [];
    #committedWords: TranscriptWord[] = [];
    #resolveFinalCommit: (() => void) | null = null;
    #startPromise: Promise<void> | null = null;
    #closed = false;

    constructor(micTrack: MediaStreamTrack) {
        this.#micTrack = micTrack;
        this.#feed = new ScribeAudioFeed((base64Pcm16) => this.#handleAudioChunk(base64Pcm16));
    }

    /**
     * Begin the async startup (mic feed → token → socket) in the background.
     * Startup failure is non-fatal — it's reported and recording proceeds without a
     * live transcript, so the returned promise resolves rather than rejects.
     * pause/resume/finalize/abort work whether or not startup has finished.
     */
    start(): Promise<void> {
        if (!this.#startPromise) {
            this.#startPromise = this.#start();
        }
        return this.#startPromise;
    }

    async #start(): Promise<void> {
        const micTrack = this.#micTrack;
        try {
            // Begin capturing straight away so t=0 is the recording start. Until
            // the socket opens, frames accumulate in `#pendingChunks`. Each
            // `#closed` check bails if we were torn down (abort/finalize) while
            // an await was in flight.
            await this.#feed.start(micTrack);
            if (this.#closed) return;

            const { Scribe, CommitStrategy, RealtimeEvents, AudioFormat } =
                await import('@elevenlabs/client');
            if (this.#closed) return;

            const tokenResponse = await ApiService.transcriptionTokenCreate({});
            if (this.#closed) return;
            const token = tokenResponse.data.token;
            if (!token) {
                throw new Error('ScribeRealtimeClient: backend returned no token');
            }

            const connection = Scribe.connect({
                token,
                modelId: SCRIBE_MODEL_ID,
                includeTimestamps: true,
                commitStrategy: CommitStrategy.VAD,
                audioFormat: AudioFormat.PCM_16000,
                sampleRate: SCRIBE_SAMPLE_RATE,
            });
            this.#connection = connection;

            connection.on(RealtimeEvents.OPEN, () => {
                this.#isOpen = true;
                // Flush everything captured before the socket opened, in order, so
                // Scribe receives one contiguous stream from the recording's start.
                try {
                    for (const chunk of this.#pendingChunks) {
                        connection.send({ audioBase64: chunk });
                    }
                } catch {
                    this.#stopFeeding();
                }
                this.#pendingChunks.length = 0;
            });

            connection.on(RealtimeEvents.CLOSE, () => this.#stopFeeding());

            connection.on(RealtimeEvents.COMMITTED_TRANSCRIPT_WITH_TIMESTAMPS, (data) => {
                if (data.words && data.words.length > 0) {
                    for (const word of data.words) {
                        if (typeof word.text !== 'string') continue;
                        if (typeof word.start !== 'number' || typeof word.end !== 'number')
                            continue;
                        this.#committedWords.push({
                            word: word.text,
                            startTime: word.start,
                            endTime: word.end,
                            filler: false,
                        });
                    }
                }
                this.#resolveFinalCommit?.();
                this.#resolveFinalCommit = null;
            });
        } catch (error) {
            this.#stopFeeding();
            // If we were torn down (abort/finalize) mid-startup, that's expected;
            // otherwise startup genuinely failed and there's no live transcript.
            if (!this.#closed) {
                this.#closed = true;
                ErrorReporter.capture(error);
            }
        }
    }

    #handleAudioChunk(base64Pcm16: string): void {
        if (this.#closed) return;
        const connection = this.#connection;
        if (!this.#isOpen || !connection) {
            this.#pendingChunks.push(base64Pcm16);
            return;
        }
        try {
            connection.send({ audioBase64: base64Pcm16 });
        } catch {
            this.#stopFeeding();
        }
    }

    #stopFeeding(): void {
        this.#isOpen = false;
        this.#pendingChunks.length = 0;
        this.#feed.stop();
    }

    pause(): void {
        if (this.#closed) return;
        this.#feed.pause();
    }

    resume(): void {
        if (this.#closed) return;
        this.#feed.resume();
    }

    stopCapturing(): void {
        if (this.#closed) return;
        this.#feed.stop();
    }

    getCommittedWords(): TranscriptWord[] {
        return [...this.#committedWords];
    }

    /**
     * Stop feeding, send a final commit, wait (up to 5s) for the matching
     * word-timed event, then close the socket and return the transcript words.
     *
     * `audioFileDurationMillis` (the recorded media's duration) bounds the
     * transcript so no word extends past the audio
     */
    async finalize(audioFileDurationMillis: number): Promise<TranscriptWord[]> {
        if (this.#closed) return this.#boundedWords(audioFileDurationMillis);
        this.#closed = true;
        this.#feed.stop();

        const connection = this.#connection;
        if (!connection) {
            // Startup never reached the socket, so nothing was committed.
            return this.#boundedWords(audioFileDurationMillis);
        }

        const finalCommitPromise = new Promise<void>((resolve) => {
            this.#resolveFinalCommit = resolve;
        });

        let committed = false;
        try {
            connection.commit();
            committed = true;
        } catch {
            // commit can throw if the socket is already closed — ignore and
            // skip the wait below since no event will ever arrive.
        }

        if (committed) {
            await Promise.race([
                finalCommitPromise,
                new Promise<void>((resolve) => setTimeout(resolve, FINALIZE_TIMEOUT_MS)),
            ]);
        }

        connection.close();
        return this.#boundedWords(audioFileDurationMillis);
    }

    #boundedWords(audioFileDurationMillis: number): TranscriptWord[] {
        return clampWordsToDuration(this.getCommittedWords(), audioFileDurationMillis);
    }

    abort(): void {
        if (this.#closed) return;
        this.#closed = true;
        this.#feed.stop();
        this.#connection?.close();
    }
}
